import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { persistProfilePref } from '../../utils/persistProfilePref';

/**
 * SortableHeaderButtons — drag-to-reorder for the top-bar action cluster.
 * Feb 26, 2026.
 *
 * The provider asked for the top toolbar buttons to be user-reorderable so
 * her most-used actions can sit in the easiest position. This wrapper:
 *   • Persists the order to `writeright_header_button_order` localStorage
 *   • Uses a 200ms press-and-hold gesture to distinguish click from drag
 *     (quick tap → normal click, long-press → drag handle)
 *   • Auto-reconciles with the current button set on mount: any new buttons
 *     appended; removed buttons dropped from the saved order
 *
 * USAGE:
 *   <SortableHeaderButtons
 *     storageKey="writeright_header_button_order"
 *     buttons={[
 *       { id: 'next_patient', render: () => <Button ...>...</Button> },
 *       { id: 'update',       render: () => <Button ...>...</Button> },
 *       ...
 *     ]}
 *   />
 *
 * The `id` is stable per logical action (NOT a DOM id). Order them in the
 * default reading order; user overrides override.
 */

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
    touchAction: 'manipulation',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-sortable-item={id}
    >
      {children}
    </div>
  );
};

const reconcileOrder = (saved, currentIds) => {
  if (!Array.isArray(saved) || saved.length === 0) return currentIds;
  const valid = saved.filter((id) => currentIds.includes(id));
  const missing = currentIds.filter((id) => !valid.includes(id));
  return [...valid, ...missing];
};

const SortableHeaderButtons = ({ buttons, storageKey = 'writeright_header_button_order' }) => {
  const currentIds = (buttons || []).map((b) => b.id);

  const [order, setOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return reconcileOrder(parsed, currentIds);
      }
    } catch (e) { /* ignore */ }
    return currentIds;
  });

  // If the set of button ids changes mid-session (e.g., user just signed in
  // and Stats appears), reconcile.
  useEffect(() => {
    setOrder((prev) => reconcileOrder(prev, currentIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIds.join(',')]);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(order)); }
    catch (e) { /* ignore */ }
    // Feb 28, 2026 — server-persist so drag order follows across devices.
    // persistProfilePref is debounced (350ms) so a quick drag doesn't
    // spam the backend with intermediate orderings.
    persistProfilePref({ header_button_order: order });
  }, [order, storageKey]);

  // 200ms press-and-hold activation so quick taps fire the underlying click,
  // while a deliberate long-press starts a drag. 6px tolerance suppresses
  // accidental micro-movements.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrder((prev) => {
      const oldIdx = prev.indexOf(active.id);
      const newIdx = prev.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }, []);

  const renderById = (id) => {
    const b = buttons.find((x) => x.id === id);
    return b ? b.render() : null;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={order} strategy={horizontalListSortingStrategy}>
        {order.map((id) => (
          <SortableItem key={id} id={id}>
            {renderById(id)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default SortableHeaderButtons;
